import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { MyButton } from '@interstellar-component'
import { Mail01 } from '@untitled-ui/icons-react'

import { RegisterCompanyService } from '../service'

/**
 * Shown after the membership agreement is emailed to the signer (DocuSign email
 * mode), and on resume when an envelope is already awaiting signature — so the
 * user can't re-submit the TnC. Auto-advances to waiting-approval once the signer
 * completes: polls signing-status on mount and whenever the tab regains focus
 * (the user signs in their mail client, then returns to this tab).
 */
export default function StepCheckEmail({ signerEmail, companyId, onDone }) {
  useEffect(() => {
    if (!companyId) return undefined
    let cancelled = false

    const check = async () => {
      try {
        const res = await RegisterCompanyService.signingStatus(companyId)
        if (!cancelled && res?.data?.status === 'completed') onDone()
      } catch {
        // No envelope yet / not completed — ignore and wait for the next focus.
      }
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }

    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', check)
    check()

    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', check)
    }
  }, [companyId, onDone])

  return (
    <section className="flex h-full w-full items-center justify-center px-6">
      <div className="flex max-w-[420px] flex-col items-center gap-6 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-brand/50 text-brand/700">
          <Mail01 className="size-7" stroke="currentColor" aria-hidden />
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-gray-900">Check your email</h1>
          <p className="text-gray-600">
            We have sent the membership agreement to{' '}
            <span className="font-medium text-gray-900">
              {signerEmail || "the authorized representative's email"}
            </span>
            . Open that email and sign it to continue — this page moves on
            automatically once you have signed.
          </p>
          <p className="text-sm text-gray-500">
            After you sign, CLIK reviews your application and then countersigns the agreement.
            You can activate the company with your CLIK credentials once CLIK has signed.
          </p>
        </div>
        <div className="w-full">
          <MyButton
            type="button"
            color="primary"
            variant="filled"
            size="lg"
            expanded
            onClick={onDone}
          >
            <span className="text-base font-semibold text-white">
              Back to the company page
            </span>
          </MyButton>
        </div>
      </div>
    </section>
  )
}

StepCheckEmail.propTypes = {
  signerEmail: PropTypes.string,
  companyId: PropTypes.string,
  onDone: PropTypes.func.isRequired,
}

StepCheckEmail.defaultProps = {
  signerEmail: '',
  companyId: null,
}
